import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Clock, 
  CheckCircle, 
  ChevronDown,
  History,
  Settings,
  X
} from 'lucide-react';
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  updateDoc, 
  doc,
  setDoc,
  getDocs,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { toast } from 'react-hot-toast';
import { Order } from '../types';
import { isCurrentlyOpen } from '../utils/checkOpenStatus';
import { useOpeningHours } from '../contexts/OpeningHoursContext';
import ConfirmModal from '../components/ConfirmModal';

const OrderPanel: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [onlineOrderingEnabled, setOnlineOrderingEnabled] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalType, setConfirmModalType] = useState<'enable' | 'disable'>('enable');
  const [wakeLock, setWakeLock] = useState<any>(null);
  const [keepAwakeEnabled, setKeepAwakeEnabled] = useState(false);
  const wakeLockSupportedRef = useRef<boolean>('wakeLock' in navigator);
  const keepAwakeEnabledRef = useRef<boolean>(false);
  const soundEnabledRef = useRef<boolean>(false);
  const audioInitializedRef = useRef<boolean>(false);
  const isMountedRef = useRef<boolean>(false);
  const isVisibleRef = useRef<boolean>(document.visibilityState === 'visible');
  const previousOrderIdsRef = useRef<Set<string>>(new Set());
  const audioContextRef = useRef<AudioContext | null>(null);
  const beepIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [hasPendingOrders, setHasPendingOrders] = useState(false);
  const [showOrderHistory, setShowOrderHistory] = useState(false);
  const [allOrders, setAllOrders] = useState<Order[]>([]);
  const [soundEnabled, setSoundEnabled] = useState(true); // Start enabled by default
  const [audioInitialized, setAudioInitialized] = useState(false);
  const [showAudioPrompt, setShowAudioPrompt] = useState(true);
  const [showTestSpeechButton, setShowTestSpeechButton] = useState(false);
  const [activeTab, setActiveTab] = useState<'orders' | 'history'>('orders');
  const { openingHours } = useOpeningHours();
  const [showQuickMenu, setShowQuickMenu] = useState(false);

  useEffect(() => {
    keepAwakeEnabledRef.current = keepAwakeEnabled;
  }, [keepAwakeEnabled]);
  
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
    
    // Preload voices when sound is enabled (important for iOS)
    if (soundEnabled && 'speechSynthesis' in window) {
      // Trigger voice loading
      const voices = window.speechSynthesis.getVoices();
      console.log('🗣️ Voices loaded:', voices.length);
      
      // If no voices yet, wait for them
      if (voices.length === 0) {
        window.speechSynthesis.addEventListener('voiceschanged', () => {
          const loadedVoices = window.speechSynthesis.getVoices();
          console.log('🗣️ Voices loaded after change:', loadedVoices.length);
        }, { once: true });
      }
    }
  }, [soundEnabled]);
  
  useEffect(() => {
    audioInitializedRef.current = audioInitialized;
  }, [audioInitialized]);
  
  const keepAwakeVideoRef = useRef<HTMLVideoElement | null>(null);

  // Function to initialize AudioContext (needs user interaction on iOS)
  const initializeAudioContext = useCallback(async () => {
      try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        console.log('🔊 AudioContext initialized');
      }
      
      const audioContext = audioContextRef.current;
      
      // Resume the context (required for iOS)
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
        console.log('🔊 AudioContext resumed');
      }
      
      // Initialize speech synthesis voices (important for iOS)
      if ('speechSynthesis' in window) {
        const voices = window.speechSynthesis.getVoices();
        console.log('🗣️ Initializing voices:', voices.length);
        
        // If no voices loaded yet, trigger loading
        if (voices.length === 0) {
          window.speechSynthesis.addEventListener('voiceschanged', () => {
            const loadedVoices = window.speechSynthesis.getVoices();
            console.log('🗣️ Voices loaded:', loadedVoices.length);
          }, { once: true });
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error initializing audio:', error);
      return false;
    }
  }, []);

  // Initialize audio with user gesture - JUST BEEP, then show test speech button
  const initializeAudio = useCallback(async () => {
    const initialized = await initializeAudioContext();
    if (initialized) {
      // Play a test beep to confirm it works
      const audioContext = audioContextRef.current;
      if (audioContext) {
        const now = audioContext.currentTime;
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.setValueAtTime(880, now);
        gainNode.gain.setValueAtTime(0, now);
        gainNode.gain.linearRampToValueAtTime(0.3, now + 0.01);
        gainNode.gain.linearRampToValueAtTime(0, now + 0.15);
        
        oscillator.start(now);
        oscillator.stop(now + 0.15);
      }
      
      // Preload voices for iPhone
      if ('speechSynthesis' in window) {
        const voices = window.speechSynthesis.getVoices();
        console.log('🗣️ Voices available:', voices.length);
        
        if (voices.length === 0) {
          window.speechSynthesis.addEventListener('voiceschanged', () => {
            const loadedVoices = window.speechSynthesis.getVoices();
            console.log('🗣️ Voices loaded:', loadedVoices.length);
          }, { once: true });
        }
      }
      
      // Hide "Tap here" prompt and show "Test Speech" button
      setShowAudioPrompt(false);
      setShowTestSpeechButton(true);
      
      toast.success('Audio ready! Now tap "Test Speech" to enable voice', { icon: '🔊' });
      console.log('🔊 Audio initialized, waiting for speech test');
      
      return true;
    } else {
      toast.error('Failed to initialize audio');
      return false;
    }
  }, [initializeAudioContext]);

  // Test speech function - MUST be called directly from button click for iPhone
  const testSpeechForIPhone = useCallback(() => {
    console.log('🗣️ testSpeechForIPhone() called - DIRECT user interaction');
    
    if (!('speechSynthesis' in window)) {
      toast.error('Speech not supported on this device');
      return;
    }
    
    const synth = window.speechSynthesis;
    
    // Cancel any existing speech
    if (synth.speaking) {
      synth.cancel();
    }
    
    // Create utterance IMMEDIATELY (synchronous with click) - CRITICAL for iPhone
    const utterance = new SpeechSynthesisUtterance('Audio notification on');
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    utterance.lang = 'en-US';
    
    // Add event handlers
    utterance.onstart = () => {
      console.log('✅ 🗣️ Test speech STARTED successfully - iPhone speech unlocked!');
    };
    utterance.onend = () => {
      console.log('✅ 🗣️ Test speech ENDED successfully');
      // After successful speech, complete initialization
      setAudioInitialized(true);
      setShowTestSpeechButton(false);
      toast.success('Voice notifications ready!', { icon: '🔔' });
    };
    utterance.onerror = (e) => {
      console.error('❌ 🗣️ Test speech ERROR:', e.error, e);
      // Silently handle error - don't show toast to user
    };
    
    // Get voices
    const voices = synth.getVoices();
    console.log('🗣️ Voices available for test:', voices.length);
    
    if (voices.length > 0) {
      const englishVoice = voices.find(v => v.lang === 'en-US') 
                         || voices.find(v => v.lang.startsWith('en-US'))
                         || voices.find(v => v.lang.startsWith('en-GB'))
                         || voices.find(v => v.lang.startsWith('en'))
                         || voices[0];
      if (englishVoice) {
        utterance.voice = englishVoice;
        console.log('🗣️ Using voice:', englishVoice.name, englishVoice.lang);
      }
    }
    
    // CRITICAL: Call speak() IMMEDIATELY - NO setTimeout! This MUST be synchronous with user click
    synth.speak(utterance);
    console.log('🗣️ synth.speak() called DIRECTLY from button click');
  }, []);

  // Toggle sound notifications
  const toggleSound = useCallback(async () => {
    // If audio isn't initialized yet, initialize it first
    if (!audioInitialized) {
      const success = await initializeAudio();
      if (!success) return;
    }

    if (!soundEnabled) {
      // Enabling sound
      setSoundEnabled(true);
      toast.success('Sound notifications enabled!', { icon: '🔔' });
      console.log('🔊 Sound notifications enabled');
    } else {
      // Disabling sound
      setSoundEnabled(false);
      toast.success('Sound notifications disabled', { icon: '🔕' });
      console.log('🔇 Sound notifications disabled');
    }
  }, [soundEnabled, audioInitialized, initializeAudio]);

  // Function to play notification sound with multiple beeps
  const playNotificationSound = useCallback(async () => {
    console.log('🔔 playNotificationSound() called');
    
    try {
      // Ensure we have an AudioContext available
      if (!audioContextRef.current) {
        console.log('🔊 Creating AudioContext for notification beep');
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }

      const audioContext = audioContextRef.current;
      console.log('🔔 AudioContext state:', audioContext.state);

      // Resume audio context if suspended (required for iOS)
      if (audioContext.state === 'suspended') {
        console.log('🔔 Resuming suspended AudioContext...');
        await audioContext.resume();
        console.log('🔊 AudioContext resumed, state now:', audioContext.state);
      }

      // Get current time AFTER resume
      const now = audioContext.currentTime;
      console.log('🔔 Current audio time:', now, '- Starting 3 beeps NOW');

      // Play 3 beeps immediately
      for (let i = 0; i < 3; i++) {
        const startTime = now + 0.05 + (i * 0.3); // Small 50ms offset, then 300ms gaps
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        // Higher frequency for more noticeable sound
        oscillator.frequency.setValueAtTime(880, startTime); // A5 note

        // Create envelope for each beep
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(0.5, startTime + 0.02);
        gainNode.gain.linearRampToValueAtTime(0.5, startTime + 0.12);
        gainNode.gain.linearRampToValueAtTime(0, startTime + 0.18);

        oscillator.start(startTime);
        oscillator.stop(startTime + 0.18);
        
        console.log(`🔔 Beep ${i + 1}/3 scheduled at ${startTime.toFixed(3)}`);
      }

      console.log('✅ 🔔 All 3 beeps scheduled and should play NOW');
    } catch (error) {
      console.error('❌ Error playing notification sound:', error);
    }
  }, []);

  // Function to speak text using Speech Synthesis API
  const speak = useCallback((text: string) => {
    try {
      console.log('🗣️ speak() called with text:', text);
      
      // Log warning if audio isn't initialized, but still try to speak (for iPhone compatibility)
      if (!audioInitialized) {
        console.warn('🗣️ Audio not fully initialized - attempting to speak anyway (iPhone may require "Tap here" first)');
      }

      // Check if speech synthesis is supported
      if (!('speechSynthesis' in window)) {
        console.warn('Speech synthesis not supported');
        return;
      }

      const synth = window.speechSynthesis;
      console.log('🗣️ Speech synthesis available, preparing to speak');

      // iOS-specific fix: ensure voices are loaded
      const speakWithVoice = () => {
        // Cancel any ongoing speech
        if (synth.speaking) {
          synth.cancel();
          // Give a moment for cancel to complete on iOS
          setTimeout(() => doSpeak(), 100);
        } else {
          doSpeak();
        }
      };

      const doSpeak = () => {
        console.log('🗣️ doSpeak() executing');
        const utterance = new SpeechSynthesisUtterance(text);
        
        // iOS works better with specific settings
        utterance.rate = 1.0; // Normal speed
        utterance.pitch = 1.0;
        utterance.volume = 1.0;
        utterance.lang = 'en-US'; // Set explicit language
        
        // Get voices and use the first English voice if available
        const voices = synth.getVoices();
        console.log('🗣️ Available voices for speaking:', voices.length);
        
        // Prioritize iOS voices if available
        const englishVoice = voices.find(voice => 
          voice.lang.startsWith('en-US') || voice.lang.startsWith('en-GB') || voice.lang.startsWith('en')
        );
        if (englishVoice) {
          utterance.voice = englishVoice;
          console.log('🗣️ Using voice:', englishVoice.name, '| Lang:', englishVoice.lang);
        } else {
          console.log('🗣️ No English voice found, using default');
        }
        
        // Error handling for iOS
        utterance.onerror = (event) => {
          console.error('❌ 🗣️ Speech synthesis error:', event);
          console.error('❌ 🗣️ Error type:', event.error);
        };
        
        utterance.onstart = () => {
          console.log('✅ 🗣️ Speaking:', text);
        };
        
        utterance.onend = () => {
          console.log('✅ 🗣️ Speech complete');
        };
        
        // Speak immediately
        console.log('🗣️ Calling synth.speak() now...');
        synth.speak(utterance);
      };

      // Ensure voices are loaded (especially important for iOS)
      const voices = synth.getVoices();
      if (voices.length > 0) {
        console.log('🗣️ Voices already loaded, speaking now');
        speakWithVoice();
      } else {
        console.log('🗣️ Voices not loaded yet, waiting...');
        // Wait for voices to be loaded
        const voicesChangedHandler = () => {
          console.log('🗣️ Voices loaded via event, ready to speak');
          speakWithVoice();
        };
        synth.addEventListener('voiceschanged', voicesChangedHandler, { once: true });
        // Fallback timeout - try anyway after 1 second
        setTimeout(() => {
          console.log('🗣️ Timeout reached, trying to speak anyway');
          synth.removeEventListener('voiceschanged', voicesChangedHandler);
          speakWithVoice();
        }, 1000);
      }
    } catch (error) {
      console.error('🗣️ Error in speak function:', error);
    }
  }, [audioInitialized]);

  // Test voice function
  const testVoice = useCallback(() => {
    if (!audioInitialized) {
      toast.error('Please tap "Enable sounds & speech" first');
      return;
    }
    speak('Voice test');
  }, [audioInitialized, speak]);

  // Start/stop continuous beeping for pending orders
  useEffect(() => {
    if (hasPendingOrders && soundEnabled) {
      // Clear any existing interval before starting a new one
      if (beepIntervalRef.current) {
        clearInterval(beepIntervalRef.current);
        beepIntervalRef.current = null;
      }
      // Play notification sound IMMEDIATELY when pending orders are detected
      console.log('🔔 Playing immediate notification for pending orders');
      playNotificationSound();
      
      // Then start beeping every 20 seconds
      console.log('▶️ Starting continuous beeping for pending orders');
      beepIntervalRef.current = setInterval(() => {
        console.log('⏰ 20-second beep for pending orders');
        playNotificationSound();
      }, 20000) as unknown as NodeJS.Timeout; // 20 seconds
    } else {
      // Stop beeping
      if (beepIntervalRef.current) {
        console.log('⏹️ Stopping continuous beeping');
        clearInterval(beepIntervalRef.current);
        beepIntervalRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (beepIntervalRef.current) {
        clearInterval(beepIntervalRef.current);
      }
    };
  }, [hasPendingOrders, soundEnabled, playNotificationSound]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant' as ScrollBehavior
    });
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    const ordersQuery = query(
      collection(db, 'orders'),
      where('status', 'in', ['pending', 'making', 'ready'])
    );

    const unsubscribe = onSnapshot(ordersQuery, (snapshot) => {
      const ordersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      })) as Order[];
      
      console.log('📊 Orders snapshot received:', {
        totalOrders: ordersData.length,
        previousCount: previousOrderIdsRef.current.size,
        isFirstLoad: loading
      });
      
      // Check for new orders (skip on first load)
      if (previousOrderIdsRef.current.size > 0) {
        const newOrders = ordersData.filter(order => 
          !previousOrderIdsRef.current.has(order.id) && order.status === 'pending'
        );
        
        if (newOrders.length > 0) {
          console.log('🆕 NEW ORDER DETECTED!', {
            count: newOrders.length,
            orders: newOrders.map(o => ({ id: o.id, number: o.orderNumber }))
          });
          
          // Debug: Check audio status
          console.log('🔍 Audio Status Check:', {
            soundEnabled: soundEnabledRef.current,
            audioInitialized: audioInitializedRef.current,
            hasAudioContext: !!audioContextRef.current,
            audioContextState: audioContextRef.current?.state
          });
          
          // ALWAYS play notification sound immediately when order arrives
          // Try to play even if not fully initialized (will initialize if needed)
          const playImmediateBeep = async () => {
            try {
              if (!audioContextRef.current) {
                // If no audio context, try to create one
                console.log('🔊 Creating AudioContext for immediate beep');
                const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
                audioContextRef.current = audioCtx;
              }
              
              const audioContext = audioContextRef.current;
              console.log('🔊 AudioContext state:', audioContext.state);
              
              // CRITICAL: Resume if suspended (iOS requirement)
              if (audioContext.state === 'suspended') {
                console.log('🔊 Resuming suspended AudioContext for immediate beep...');
                await audioContext.resume();
                console.log('🔊 AudioContext resumed!');
              }
              
              // Play beep immediately
              const now = audioContext.currentTime;
              for (let i = 0; i < 3; i++) {
                const startTime = now + 0.05 + (i * 0.3);
                const oscillator = audioContext.createOscillator();
                const gainNode = audioContext.createGain();
                
                oscillator.connect(gainNode);
                gainNode.connect(audioContext.destination);
                
                oscillator.frequency.setValueAtTime(880, startTime);
                gainNode.gain.setValueAtTime(0, startTime);
                gainNode.gain.linearRampToValueAtTime(0.5, startTime + 0.02);
                gainNode.gain.linearRampToValueAtTime(0.5, startTime + 0.12);
                gainNode.gain.linearRampToValueAtTime(0, startTime + 0.18);
                
                oscillator.start(startTime);
                oscillator.stop(startTime + 0.18);
              }
              console.log('✅ 🔊 Immediate beep played for new order!');
            } catch (err) {
              console.error('❌ Could not play immediate beep:', err);
            }
          };
          
          playImmediateBeep();
          
          // Show toast notification
          newOrders.forEach(order => {
            toast.success(`New Order #${order.orderNumber} received!`, {
              duration: 5000,
              icon: '🔔',
            });
          });
        } else {
          console.log('No new orders detected');
        }
      } else {
        console.log('First load - initializing order tracking');
      }
      
      // Update previous order IDs AFTER checking for new ones
      previousOrderIdsRef.current = new Set(ordersData.map(order => order.id));
      
      // Check if there are any pending orders for continuous beeping
      const pendingCount = ordersData.filter(order => order.status === 'pending').length;
      setHasPendingOrders(pendingCount > 0);
      if (pendingCount > 0 && soundEnabledRef.current) {
        // Ensure the interval is running immediately when we detect pending orders
        if (!beepIntervalRef.current) {
          console.log('▶️ Ensuring continuous beeping starts (snapshot)');
          // Start a fresh interval
          beepIntervalRef.current = setInterval(() => {
            console.log('⏰ 20-second beep for pending orders (snapshot-started)');
            playNotificationSound();
          }, 20000) as unknown as NodeJS.Timeout;
        }
      }
      console.log(`📊 Pending orders: ${pendingCount}`);
      
      setOrders(ordersData.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()));
      setLoading(false);
    }, (error) => {
      console.error('❌ Error loading orders:', error);
      if (error.code === 'permission-denied') {
        toast.error('Permission denied. Please make sure you are logged in as admin.');
      } else {
        toast.error('Failed to load orders');
      }
      setLoading(false);
    });

    // Listen to settings
    const unsubscribeSettings = onSnapshot(doc(db, 'settings', 'ordering'), (doc) => {
      if (doc.exists()) {
        const enabled = doc.data().enabled ?? true;
        setOnlineOrderingEnabled(enabled);
        setKeepAwakeEnabled(enabled);
      }
    });

    return () => {
      isMountedRef.current = false;
      keepAwakeEnabledRef.current = false;
      unsubscribe();
      unsubscribeSettings();
    };
  }, []);

  // Fetch all orders for history (including completed ones)
  useEffect(() => {
    const allOrdersQuery = query(collection(db, 'orders'));
    
    const unsubscribeAllOrders = onSnapshot(allOrdersQuery, (snapshot) => {
      const allOrdersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      })) as Order[];
      
      // Sort by creation time (newest first)
      setAllOrders(allOrdersData.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()));
    }, (error) => {
      console.error('Error loading all orders:', error);
    });

    return () => unsubscribeAllOrders();
  }, []);


  // Keep Screen Awake
  useEffect(() => {
    let wakeLockInstance: any = wakeLock;

    const acquireWakeLock = async () => {
      try {
        if (wakeLockSupportedRef.current) {
          wakeLockInstance = await (navigator as any).wakeLock.request('screen');
          setWakeLock(wakeLockInstance);
          wakeLockInstance.addEventListener('release', () => {
            console.log('🔅 Screen wake lock released');
          });
          console.log('🔆 Screen wake lock activated');
        } else {
          console.warn('⚠️ Wake Lock API not supported, using video fallback');
          // Fallback: try to play an offscreen tiny looping video if available
          if (keepAwakeVideoRef.current) {
            try {
              await keepAwakeVideoRef.current.play();
              console.log('🔆 Fallback video keep-awake started');
            } catch (e) {
              console.warn('⚠️ Fallback keep-awake failed to start', e);
            }
          }
        }
      } catch (err) {
        console.error('❌ Failed to acquire wake lock', err);
        // Try video fallback
        if (keepAwakeVideoRef.current) {
          try {
            await keepAwakeVideoRef.current.play();
            console.log('🔆 Using video fallback after wake lock failed');
          } catch (e) {
            console.warn('⚠️ Video fallback also failed', e);
          }
        }
      }
    };

    const releaseWakeLock = async () => {
      try {
        if (wakeLockInstance) {
          await wakeLockInstance.release();
          setWakeLock(null);
          console.log('🔅 Screen wake lock released');
        }
        if (keepAwakeVideoRef.current) {
          keepAwakeVideoRef.current.pause();
          keepAwakeVideoRef.current.currentTime = 0;
        }
      } catch (err) {
        console.error('⚠️ Error releasing wake lock', err);
      }
    };

    const handleVisibilityChange = async () => {
      isVisibleRef.current = document.visibilityState === 'visible';
      if (document.visibilityState === 'visible') {
        if (keepAwakeEnabled) {
          // Re-acquire on visibility
          acquireWakeLock();
        }
        
        // Resume AudioContext when page becomes visible (critical for iPhone)
        if (audioInitializedRef.current && audioContextRef.current && audioContextRef.current.state === 'suspended') {
          try {
            await audioContextRef.current.resume();
            console.log('🔊 AudioContext resumed on visibility change');
          } catch (err) {
            console.error('⚠️ Error resuming AudioContext:', err);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    if (keepAwakeEnabled) {
      acquireWakeLock();
    } else {
      releaseWakeLock();
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      releaseWakeLock();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keepAwakeEnabled]);


  const [processingOrders, setProcessingOrders] = useState<Set<string>>(new Set());
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());

  const toggleOrderExpansion = (orderId: string) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const handleOrderAction = async (orderId: string, action: 'making' | 'ready' | 'completed') => {
    try {
      setProcessingOrders(prev => new Set(prev).add(orderId));
      
      // Get the order details before updating (for voice announcement)
      const order = orders.find(o => o.id === orderId);
      const orderNumber = order?.orderNumber || 0;
      
      await updateDoc(doc(db, 'orders', orderId), {
        status: action,
        updatedAt: new Date()
      });
      
      if (action === 'making') {
        toast.success('Order is being prepared!');
      } else if (action === 'ready') {
        toast.success('Order is ready!');
        // Announce that order is ready to collect
        speak(`Order number ${orderNumber} is ready to collect`);
      } else {
        toast.success('Order completed!');
      }
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order');
    } finally {
      setProcessingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'making': return 'bg-blue-100 text-blue-800';
      case 'ready': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleToggleOnlineOrdering = () => {
    // Recompute shop open state on each click using latest openingHours
    try {
      const shopIsOpen = isCurrentlyOpen(openingHours);
      if (!onlineOrderingEnabled && !shopIsOpen) {
        toast.error('Cannot enable online ordering: shop is currently closed (see Opening Hours).', {
          duration: 5000,
          icon: '⏰'
        });
        return;
      }
    } catch (e) {
      // If hours unavailable, fall back to allowing toggle
    }
    // Show confirmation modal for both enabling and disabling
    if (onlineOrderingEnabled) {
      setConfirmModalType('disable');
      setShowConfirmModal(true);
    } else {
      setConfirmModalType('enable');
      setShowConfirmModal(true);
    }
  };

  const confirmToggleOrdering = async () => {
    setSavingSettings(true);
    setShowConfirmModal(false);
    
    try {
      if (onlineOrderingEnabled) {
        // Disabling online ordering - keep existing orders
        console.log('Disabling online ordering. Existing orders will remain visible.');
        
        await setDoc(doc(db, 'settings', 'ordering'), {
          enabled: false,
          updatedAt: new Date()
        });
        
        toast.success('Online ordering disabled. Existing orders remain visible until completed.');
      } else {
        // Enabling online ordering - cancel all previous orders and start fresh
        console.log('Enabling online ordering and clearing all order history...');
        
        // Get all orders
        const ordersRef = collection(db, 'orders');
        const ordersSnapshot = await getDocs(ordersRef);
        
        // Cancel (remove) each order
        const deletePromises = ordersSnapshot.docs.map(orderDoc => 
          deleteDoc(doc(db, 'orders', orderDoc.id))
        );
        
        await Promise.all(deletePromises);
        console.log(`Cancelled ${deletePromises.length} orders from database`);
        
        // Update settings to enable
        await setDoc(doc(db, 'settings', 'ordering'), {
          enabled: true,
          updatedAt: new Date()
        });
        
        toast.success(`Online ordering enabled! Order history cleared. New orders will start from #1.`);
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'making': return <Clock className="h-4 w-4" />;
      case 'ready': return <CheckCircle className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-3">
        <div className="max-w-7xl mx-auto">
          {/* Audio Initialization Prompt - Sticky at Top */}
          {onlineOrderingEnabled && !audioInitialized && showAudioPrompt && (
            <div className="sticky top-0 z-50 bg-gray-50 pb-3 mb-2">
              <button
                onClick={initializeAudio}
                className="w-full text-center flex items-center justify-center gap-2 bg-gradient-to-r from-red-50 to-orange-50 border border-red-300 text-red-800 px-3 py-2 rounded-lg shadow-sm active:scale-[0.98] hover:from-red-100 hover:to-orange-100 transition-all"
              >
                <span className="text-xl animate-bounce">🔊</span>
                <span className="text-sm font-semibold">Tap here to enable sounds & speech</span>
              </button>
            </div>
          )}

          {/* Test Speech Button - Shows after audio initialization, before full setup */}
          {onlineOrderingEnabled && !audioInitialized && showTestSpeechButton && (
            <div className="sticky top-0 z-50 bg-gray-50 pb-3 mb-2">
              <button
                onClick={testSpeechForIPhone}
                className="w-full text-center flex items-center justify-center gap-2 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-300 text-purple-800 px-3 py-2 rounded-lg shadow-sm active:scale-[0.98] hover:from-purple-100 hover:to-pink-100 transition-all"
              >
                <span className="text-xl animate-pulse">🗣️</span>
                <span className="text-sm font-semibold">Tap to Test Speech & Enable Voice</span>
              </button>
            </div>
          )}

          {/* Compact Tab Bar - Hidden after audio initialization */}
          {!audioInitialized && (
            <div className="bg-white rounded-lg shadow-md mb-6 overflow-hidden">
            <div className="flex items-center justify-between p-1.5 gap-0.5">
              {/* Online Ordering Toggle */}
              <button
                onClick={handleToggleOnlineOrdering}
                disabled={savingSettings}
                className={`flex flex-col items-center justify-center gap-0.5 px-1 py-2 rounded-md transition-all flex-1 min-w-0 ${
                  onlineOrderingEnabled 
                    ? 'bg-green-50 hover:bg-green-100' 
                    : 'bg-red-50 hover:bg-red-100'
                } ${savingSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span className="text-xl leading-none">🛒</span>
                <span className={`text-[10px] font-bold leading-tight ${
                  onlineOrderingEnabled ? 'text-green-600' : 'text-red-600'
                }`}>
                  {onlineOrderingEnabled ? 'ON' : 'OFF'}
                </span>
              </button>

              {/* Sound Notifications Toggle */}
              {audioInitialized && (
                <button
                  onClick={toggleSound}
                  className={`flex flex-col items-center justify-center gap-0.5 px-1 py-2 rounded-md transition-all flex-1 min-w-0 ${
                    soundEnabled 
                      ? 'bg-blue-50 hover:bg-blue-100' 
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <span className="text-xl leading-none">{soundEnabled ? '🔔' : '🔕'}</span>
                  <span className={`text-[10px] font-bold leading-tight ${
                    soundEnabled ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    {soundEnabled ? 'ON' : 'OFF'}
                  </span>
                </button>
              )}

              {/* Test Voice Button */}
              {audioInitialized && (
                <button
                  onClick={() => speak('Order number 1 is ready to collect')}
                  className="flex flex-col items-center justify-center gap-0.5 px-1 py-2 rounded-md transition-all flex-1 min-w-0 bg-purple-50 hover:bg-purple-100"
                >
                  <span className="text-xl leading-none">🗣️</span>
                  <span className="text-[10px] font-bold text-purple-600 leading-tight">TEST</span>
                </button>
              )}

              {/* Order History Toggle */}
              <button
                onClick={() => {
                  setShowOrderHistory(!showOrderHistory);
                  setActiveTab(showOrderHistory ? 'orders' : 'history');
                }}
                className={`flex flex-col items-center justify-center gap-0.5 px-1 py-2 rounded-md transition-all flex-1 min-w-0 ${
                  showOrderHistory 
                    ? 'bg-orange-50 hover:bg-orange-100' 
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <span className="text-xl leading-none">📋</span>
                <span className={`text-[10px] font-bold leading-tight ${
                  showOrderHistory ? 'text-orange-600' : 'text-gray-500'
                }`}>
                  {showOrderHistory ? 'OPEN' : 'VIEW'}
                </span>
              </button>
            </div>
            </div>
          )}

          {/* Floating Quick Menu Button - Shows after audio initialization */}
          {audioInitialized && (
            <>
              <button
                onClick={() => setShowQuickMenu(!showQuickMenu)}
                className="fixed bottom-6 right-6 z-50 bg-red-600 hover:bg-red-700 text-white p-2.5 rounded-full shadow-lg transition-all active:scale-95"
              >
                {showQuickMenu ? <X className="h-4 w-4" /> : <Settings className="h-4 w-4" />}
              </button>

              {/* Quick Menu Overlay */}
              {showQuickMenu && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowQuickMenu(false)}>
                  <div className="fixed bottom-20 right-6 bg-white rounded-lg shadow-xl p-3 min-w-[180px]" onClick={(e) => e.stopPropagation()}>
                    <div className="space-y-2">
                      {/* Sounds Toggle */}
                      <button
                        onClick={() => {
                          toggleSound();
                          setShowQuickMenu(false);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all ${
                          soundEnabled 
                            ? 'bg-blue-50 hover:bg-blue-100' 
                            : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                      >
                        <span className="text-sm font-medium">Sounds</span>
                        <span className={`text-xs font-bold ${
                          soundEnabled ? 'text-green-600' : 'text-gray-500'
                        }`}>
                          {soundEnabled ? 'ON' : 'OFF'}
                        </span>
                      </button>

                      {/* Order History */}
                      <button
                        onClick={() => {
                          setShowOrderHistory(!showOrderHistory);
                          setShowQuickMenu(false);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 bg-orange-50 hover:bg-orange-100 rounded-lg transition-all"
                      >
                        <span className="text-sm font-medium">Order History</span>
                        <History className="h-4 w-4 text-orange-600" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Order History Section */}
          {showOrderHistory && (
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Order History</h3>
                <button
                  onClick={() => setShowOrderHistory(false)}
                  className="text-gray-500 hover:text-gray-700 text-sm"
                >
                  Close
                </button>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {allOrders.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No order history available</p>
                ) : (
                  allOrders.map((order) => (
                    <div key={order.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className="text-sm">
                          <span className="font-medium">Order #{order.orderNumber}</span>
                          <span className="text-gray-500 ml-2">
                            {order.createdAt.toLocaleDateString()} {order.createdAt.toLocaleTimeString()}
                          </span>
                        </div>
                        <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        </div>
                        {order.serviceType && (
                          <div className="ml-2 px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-800">
                            {order.serviceType === 'dine-in' ? 'Dine In' : 'Takeaway'}
                          </div>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">
                        {order.items.length} item{order.items.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          <div className="max-w-6xl mx-auto">
            {/* Orders List */}
            <div id="live-orders-section">
              <div className="bg-white rounded-lg shadow-sm">
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center justify-center gap-2">
                    <h2 className="text-xl font-semibold">Live Orders</h2>
                    {/* Online Ordering Toggle - Small, no text */}
                    <button
                      onClick={handleToggleOnlineOrdering}
                      disabled={savingSettings}
                      className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${
                        onlineOrderingEnabled ? 'bg-green-600' : 'bg-gray-300'
                      } ${savingSettings ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white transition-transform ${
                        onlineOrderingEnabled ? 'translate-x-4' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                </div>

                <div className="divide-y divide-gray-200">
                  {orders.length === 0 ? (
                    <div className="p-8 text-center">
                      <Clock className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No orders at the moment</p>
                    </div>
                  ) : (
                    orders.map((order) => {
                      const isExpanded = expandedOrders.has(order.id);
                      return (
                      <motion.div
                        key={order.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        {/* Collapsed View - Order Number, Time, and Buttons */}
                        <div 
                          className="p-4 cursor-pointer flex items-center justify-between"
                          onClick={() => toggleOrderExpansion(order.id)}
                        >
                          <div className="flex items-center space-x-4 flex-1">
                          <div>
                              <h3 className="font-bold text-lg">Order #{order.orderNumber}</h3>
                              <p className="text-xs text-gray-500">
                                {order.createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </p>
                              {/* Payment Status */}
                              {(order as any).paymentMethod === 'counter' ? (
                                <p className="text-xs font-bold text-orange-600 mt-1">
                                  💵 PAY AT COUNTER
                                </p>
                              ) : (
                                <p className="text-xs font-medium text-green-600 mt-1">
                                  ✓ Paid Online
                                </p>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </div>
                        </div>

                        {/* Action Buttons - Always visible */}
                        <div className="px-4 pb-4 flex justify-end items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => handleOrderAction(order.id, 'making')}
                            disabled={order.status !== 'pending' || processingOrders.has(order.id)}
                            className={`btn btn-sm ${
                              order.status === 'making' || order.status === 'ready' || order.status === 'completed'
                                ? 'bg-green-600 text-white hover:bg-green-700 cursor-default'
                                : 'btn-primary'
                            }`}
                          >
                            {order.status === 'pending' && processingOrders.has(order.id) ? 'Processing...' : 'Making'}
                          </button>
                          
                          <button
                            onClick={() => handleOrderAction(order.id, 'ready')}
                            disabled={order.status !== 'making' || processingOrders.has(order.id)}
                            className={`btn btn-sm ${
                              order.status === 'ready' || order.status === 'completed'
                                ? 'bg-green-600 text-white hover:bg-green-700 cursor-default'
                                : order.status === 'pending'
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'btn-primary'
                            }`}
                          >
                            {order.status === 'making' && processingOrders.has(order.id) ? 'Processing...' : 'Ready'}
                          </button>
                          
                          <button
                            onClick={() => handleOrderAction(order.id, 'completed')}
                            disabled={order.status !== 'ready' || processingOrders.has(order.id)}
                            className={`btn btn-sm ${
                              order.status === 'completed'
                                ? 'bg-green-600 text-white hover:bg-green-700 cursor-default'
                                : order.status === 'pending' || order.status === 'making'
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'btn-primary'
                            }`}
                          >
                            {order.status === 'ready' && processingOrders.has(order.id) ? 'Processing...' : 'Complete'}
                          </button>

                          {/* Cancel Order */}
                          <button
                            onClick={async () => {
                              try {
                                const confirmed = window.confirm(`Cancel Order #${order.orderNumber}? This cannot be undone.`);
                                if (!confirmed) return;
                                await deleteDoc(doc(db, 'orders', order.id));
                                toast.success(`Order #${order.orderNumber} cancelled`);
                              } catch (err) {
                                console.error('Error deleting order:', err);
                                toast.error('Failed to delete order');
                              }
                            }}
                            className="btn btn-sm bg-red-600 hover:bg-red-700 text-white"
                          >
                            Delete
                          </button>
                        </div>

                        {/* Expanded Details */}
                        {isExpanded && (
                        <div className="px-4 pb-4 border-t border-gray-200 pt-4"  onClick={(e) => e.stopPropagation()}>

                        {/* Order Items */}
                        <div className="mb-4 bg-gray-50 rounded-lg p-3">
                          <h4 className="font-medium text-sm mb-2">Items:</h4>
                          <div className="space-y-2">
                            {order.items.map((item, index) => (
                              <div key={index} className="text-sm">
                                <div>
                                  <span className="font-medium">{item.quantity}x {item.menuItem.name}</span>
                                </div>
                                {item.selectedExtras && item.selectedExtras.length > 0 && (
                                  <div className="ml-4 text-xs text-gray-500">
                                    {item.selectedExtras.map((extra, idx) => (
                                      <div key={idx}>+ {extra.quantity}x {extra.extra.name}</div>
                                    ))}
                                  </div>
                                )}
                                {item.notes && (
                                  <div className="ml-4 text-xs text-gray-500 italic">Note: {item.notes}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="mb-4">
                            <p className="text-sm font-medium text-gray-700">Service</p>
                            <p className="text-sm text-gray-600 capitalize">
                              {order.serviceType.replace('-', ' ')}
                            </p>
                        </div>

                        {/* Payment Method Display */}
                        {(order as any).paymentMethod && (order as any).paymentMethod === 'counter' && (
                          <div className="mb-4 bg-orange-50 border border-orange-200 rounded p-3">
                            <p className="text-sm font-bold text-orange-800 flex items-center">
                              💵 Pay at Counter
                            </p>
                            <p className="text-xs text-orange-700 mt-1">
                              Collect payment when customer picks up order
                            </p>
                          </div>
                        )}

                        {order.notes && (
                          <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded p-2">
                            <p className="text-xs font-medium text-yellow-800">Special Instructions:</p>
                            <p className="text-sm text-yellow-700">{order.notes}</p>
                          </div>
                        )}
                        </div>
                        )}
                      </motion.div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmToggleOrdering}
        title={confirmModalType === 'enable' ? 'Enable Online Ordering' : 'Disable Online Ordering'}
        message={
          confirmModalType === 'enable'
            ? '⚠️ Warning: This will cancel all order history and start order numbers from #1. Make sure all previous orders are completed. Are you ready to enable online ordering?'
            : '⚠️ This will prevent customers from placing new orders. Existing orders will remain visible until you complete them. Do you want to continue?'
        }
        confirmText={confirmModalType === 'enable' ? 'Yes, Enable and Clear History' : 'Yes, Disable Online Ordering'}
        cancelText="Cancel"
      />

      {/* Hidden tiny video element for fallback keep-awake on unsupported devices */}
      <video
        ref={keepAwakeVideoRef}
        muted
        playsInline
        loop
        style={{ position: 'fixed', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
        src="data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAAIZnJlZQAAAu1tZGF0AAACrQYF//+p3EXpvebZSLeWLNgg2SPu73gyNjQgLSBjb3JlIDE1MiByMjg1NCBlOWE1OTAzIC0gSC4yNjQvTVBFRy00IEFWQyBjb2RlYyAtIENvcHlsZWZ0IDIwMDMtMjAxNyAtIGh0dHA6Ly93d3cudmlkZW9sYW4ub3JnL3gyNjQuaHRtbCAtIG9wdGlvbnM6IGNhYmFjPTEgcmVmPTMgZGVibG9jaz0xOjA6MCBhbmFseXNlPTB4MzoweDExMyBtZT1oZXggc3VibWU9NyBwc3k9MSBwc3lfcmQ9MS4wMDowLjAwIG1peGVkX3JlZj0xIG1lX3JhbmdlPTE2IGNocm9tYV9tZT0xIHRyZWxsaXM9MSA4eDhkY3Q9MSBjcW09MCBkZWFkem9uZT0yMSwxMSBmYXN0X3Bza2lwPTEgY2hyb21hX3FwX29mZnNldD0tMiB0aHJlYWRzPTMgbG9va2FoZWFkX3RocmVhZHM9MSBzbGljZWRfdGhyZWFkcz0wIG5yPTAgZGVjaW1hdGU9MSBpbnRlcmxhY2VkPTAgYmx1cmF5X2NvbXBhdD0wIGNvbnN0cmFpbmVkX2ludHJhPTAgYmZyYW1lcz0zIGJfcHlyYW1pZD0yIGJfYWRhcHQ9MSBiX2JpYXM9MCBkaXJlY3Q9MSB3ZWlnaHRiPTEgb3Blbl9nb3A9MCB3ZWlnaHRwPTIga2V5aW50PTI1MCBrZXlpbnRfbWluPTI1IHNjZW5lY3V0PTQwIGludHJhX3JlZnJlc2g9MCByY19sb29rYWhlYWQ9NDAgcmM9Y3JmIG1idHJlZT0xIGNyZj0yMy4wIHFjb21wPTAuNjAgcXBtaW49MCBxcG1heD02OSBxcHN0ZXA9NCBpcF9yYXRpbz0xLjQwIGFxPTE6MS4wMACAAAAA1mWIhAAz//727L4FNf2f0JcRLMXaSnA+KqSAgHc0wAAAAwAAAwAAFgn0I7DkqgN3QAAAHGAFBCwCPCVC2eAuSgAAAAMAAAMAAAMAAAMAAAMAAAMAAAMAABYJ9BZ4gtAAAAAtQZokbEFv/yQAAB+vc2hqx8rAEyb7k0M0P8XvR8wAAAAJQZ5CeIK/AAADLAAAAAQZ5kQ4iHwAAA8oAAAGEEGaZGxBb/8kAAH8xAAAAAkBnmRkQ4iHwAAAwwAAAAQZ5kQ4iHwAAA8oAAAGEEGaZGxBb/8kAAH8xAAAAAkBnmRkQ4iHwAAAwwAAAAQZ5kQ4iHwAAA8oAAAA="
      >
      </video>
    </div>
  );
};

export default OrderPanel;

